import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import LoadDetailsModal from './LoadDetailsModal';
import {
  fetchInbox, dismissGeofenceEvent, dismissGeofenceEventsForLoad,
  markNotificationRead, markAllNotificationsRead, describeGeofenceEvent, ageText,
} from '../services/alertInboxService';
import { updateLoadStatusGuarded, labelOf, completionBlockText } from '../services/statusTransitions';
import { logStatusChanged } from '../services/activityLogService';

/**
 * Alert Inbox - everything the DB raised for dispatch that nobody has
 * acknowledged yet: actionable geofence events + the user's notifications.
 */
function AlertInbox({ onClose, onChanged }) {
  const [inbox, setInbox] = useState({ geofence: [], notifications: [], error: null });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [msg, setMsg] = useState(null);
  const [openLoad, setOpenLoad] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    const data = await fetchInbox();
    setInbox(data);
    setLoading(false);
    if (onChanged) onChanged();
  }, [onChanged]);

  useEffect(() => { refresh(); }, [refresh]);

  const withBusy = async (key, fn) => {
    setBusy((b) => ({ ...b, [key]: true }));
    try { await fn(); } finally { setBusy((b) => ({ ...b, [key]: false })); }
  };

  const ackEvent = (ev) => withBusy(ev.id, async () => {
    const r = await dismissGeofenceEvent(ev.id);
    if (!r.ok) { setMsg(r.error); return; }
    setInbox((i) => ({ ...i, geofence: i.geofence.filter((x) => x.id !== ev.id) }));
    if (onChanged) onChanged();
  });

  const applySuggestion = (ev) => withBusy(ev.id, async () => {
    const s = describeGeofenceEvent(ev).suggest;
    if (!s || !ev.load_id) return;
    const r = await updateLoadStatusGuarded(ev.load_id, s.from, s.to);
    if (!r.ok) {
      setMsg(r.reason === 'conflict'
        ? `Load #${ev.load ? ev.load.load_number : ''} is already "${labelOf(r.current)}" - nothing changed.`
        : (completionBlockText(r.error) || ('Could not update the load: ' + (r.error && r.error.message ? r.error.message : ''))));
      return;
    }
    logStatusChanged({ id: ev.load_id, load_number: ev.load ? ev.load.load_number : null }, s.from, s.to);
    await dismissGeofenceEventsForLoad(ev.load_id);
    setInbox((i) => ({ ...i, geofence: i.geofence.filter((x) => x.load_id !== ev.load_id) }));
    setMsg(`Load #${ev.load ? ev.load.load_number : ''} marked ${labelOf(s.to)}.`);
    if (onChanged) onChanged();
  });

  const readNotification = (n) => withBusy(n.id, async () => {
    const r = await markNotificationRead(n.id);
    if (!r.ok) { setMsg(r.error); return; }
    setInbox((i) => ({ ...i, notifications: i.notifications.filter((x) => x.id !== n.id) }));
    if (onChanged) onChanged();
  });

  const readAll = () => withBusy('all', async () => {
    const r = await markAllNotificationsRead();
    if (!r.ok) { setMsg(r.error); return; }
    setInbox((i) => ({ ...i, notifications: [] }));
    if (onChanged) onChanged();
  });

  const openLoadById = async (loadId) => {
    if (!loadId) return;
    const { data } = await supabase.from('loads').select('*, load_stops(*)').eq('id', loadId).single();
    if (data) setOpenLoad(data);
  };

  const prioColor = (p) => p === 'action' ? 'var(--color-danger)' : p === 'warn' ? 'var(--color-warning)' : 'var(--accent-blue)';
  const btn = { padding: '0.35rem 0.7rem', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', background: 'var(--btn-secondary-bg)', color: 'var(--text-primary)', cursor: 'pointer', fontSize: '0.8rem' };
  const total = inbox.geofence.length + inbox.notifications.length;

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}>
      <div style={{ backgroundColor: 'var(--surface-1)', borderRadius: '10px', padding: '1.5rem', maxWidth: '820px', width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid var(--border-default)' }}>
          <div>
            <h2 style={{ color: 'var(--accent-primary)', margin: '0 0 0.25rem 0' }}>Alerts</h2>
            <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
              {loading ? 'Loading...' : total === 0 ? 'Nothing waiting on you.' : `${inbox.geofence.length} from the trucks, ${inbox.notifications.length} notifications`}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button style={btn} onClick={refresh} disabled={loading}>Refresh</button>
            <button style={btn} onClick={onClose}>Close</button>
          </div>
        </div>

        {msg && (
          <div style={{ marginBottom: '0.75rem', padding: '0.5rem 0.75rem', background: 'var(--surface-2)', borderRadius: 'var(--radius-md)', fontSize: '0.85rem', display: 'flex', justifyContent: 'space-between' }}>
            <span>{msg}</span><button onClick={() => setMsg(null)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>x</button>
          </div>
        )}
        {inbox.error && <div style={{ color: 'var(--color-danger)', fontSize: '0.85rem', marginBottom: '0.75rem' }}>{inbox.error}</div>}

        <div style={{ overflowY: 'auto', flex: 1 }}>
          <h3 style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', letterSpacing: '1px', textTransform: 'uppercase', margin: '0 0 0.5rem 0' }}>From the trucks</h3>
          {inbox.geofence.length === 0 && !loading && <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '1rem' }}>No unacknowledged geofence events.</div>}
          {inbox.geofence.map((ev) => {
            const d = describeGeofenceEvent(ev);
            return (
              <div key={ev.id} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', padding: '0.6rem 0', borderBottom: '1px solid var(--border-default)' }}>
                <div style={{ width: '4px', alignSelf: 'stretch', background: prioColor(d.priority), borderRadius: '2px' }} />
                <div style={{ flex: 1 }}>
                  <div style={{ color: 'var(--text-primary)', fontSize: '0.9rem' }}>{d.icon} {d.title}</div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>{d.detail} <span style={{ opacity: 0.7 }}>· {ageText(ev.created_at)}{ev.load && ev.load.status ? ` · load is ${labelOf(ev.load.status)}` : ''}</span></div>
                </div>
                <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  {d.suggest && ev.load && ev.load.status === d.suggest.from && (
                    <button style={{ ...btn, background: 'var(--color-success)', borderColor: 'var(--color-success)' }} disabled={!!busy[ev.id]} onClick={() => applySuggestion(ev)}>{d.suggest.label}</button>
                  )}
                  {ev.load_id && <button style={btn} onClick={() => openLoadById(ev.load_id)}>Open load</button>}
                  <button style={btn} disabled={!!busy[ev.id]} onClick={() => ackEvent(ev)}>Dismiss</button>
                </div>
              </div>
            );
          })}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '1.25rem 0 0.5rem 0' }}>
            <h3 style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', letterSpacing: '1px', textTransform: 'uppercase', margin: 0 }}>Notifications</h3>
            {inbox.notifications.length > 0 && <button style={btn} disabled={!!busy.all} onClick={readAll}>Mark all read</button>}
          </div>
          {inbox.notifications.length === 0 && !loading && <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>No unread notifications.</div>}
          {inbox.notifications.map((n) => (
            <div key={n.id} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', padding: '0.6rem 0', borderBottom: '1px solid var(--border-default)' }}>
              <div style={{ width: '4px', alignSelf: 'stretch', background: n.priority === 'high' || n.priority === 'urgent' ? 'var(--color-danger)' : 'var(--accent-blue)', borderRadius: '2px' }} />
              <div style={{ flex: 1 }}>
                <div style={{ color: 'var(--text-primary)', fontSize: '0.9rem' }}>{n.title || n.notification_type}</div>
                <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>{n.message} <span style={{ opacity: 0.7 }}>· {ageText(n.created_at)}</span></div>
              </div>
              <div style={{ display: 'flex', gap: '0.4rem' }}>
                {/* __PLANNING_ASSIGN__ a job moved off a driver on the Planning board: reconfirm that driver's route (the hub's
                    "Needs attention: reconfirm ..." alert; its type is load_change - the notifications CHECK has no type of its own for it) */}
                {/^Needs attention: reconfirm/.test(n.title || '') && <button style={{ ...btn, background: 'var(--color-warning)', borderColor: 'var(--color-warning)' }}
                  onClick={() => { try { window.dispatchEvent(new CustomEvent('hb-open-view', { detail: { view: 'routes' } })); } catch (e) { /* old browser */ } onClose(); }}>Open Route Builder</button>}
                {n.entity_type === 'load' && n.entity_id && <button style={btn} onClick={() => openLoadById(n.entity_id)}>Open load</button>}
                <button style={btn} disabled={!!busy[n.id]} onClick={() => readNotification(n)}>Read</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {openLoad && (
        <LoadDetailsModal
          load={openLoad}
          onClose={() => setOpenLoad(null)}
          onUpdate={() => { setOpenLoad(null); refresh(); }}
        />
      )}
    </div>
  );
}

export default AlertInbox;
